import { useEffect, useState, type RefObject } from 'react';

export interface ScrollEdges {
  /** Content is hidden past the start (left) edge. */
  start: boolean;
  /** Content is hidden past the end (right) edge. */
  end: boolean;
  /** The element scrolls horizontally at all. */
  overflowing: boolean;
}

const NONE: ScrollEdges = { start: false, end: false, overflowing: false };

/**
 * Where a horizontal scroller currently is, for edge fades and for deciding
 * whether the region needs to be keyboard-focusable at all.
 *
 * Re-measured on scroll (passive) and on resize where ResizeObserver exists;
 * jsdom has neither layout nor the observer, so there it reports "no overflow",
 * which is also the honest answer.
 */
export function useScrollEdges(ref: RefObject<HTMLElement | null>): ScrollEdges {
  const [edges, setEdges] = useState<ScrollEdges>(NONE);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const measure = (): void => {
      const overflowing = el.scrollWidth > el.clientWidth + 1;
      const next: ScrollEdges = {
        start: overflowing && el.scrollLeft > 1,
        end: overflowing && el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
        overflowing,
      };
      setEdges((prev) =>
        prev.start === next.start && prev.end === next.end && prev.overflowing === next.overflowing
          ? prev
          : next,
      );
    };

    measure();
    el.addEventListener('scroll', measure, { passive: true });
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(el);
    }
    return () => {
      el.removeEventListener('scroll', measure);
      observer?.disconnect();
    };
  }, [ref]);

  return edges;
}
